import axios from "axios";
import { MayorInfo } from "../types";

// Using local JSON file instead of external API
const MAYOR_DATA_FILE = "/data/OBT-leader.json";

// Cache to store fetched mayor data
const mayorCache = new Map<string, MayorInfo | null>();
// Cache all mayor data after first fetch
let allMayorData: MayorApiResponse[] | null = null;

interface MayorApiResponse {
  PROVINCE_NAME: string;
  AMPHUR_NAME: string;
  ORG_NAME: string;
  TERM_YEAR: string;
  NAME: string;
  START_DATE: string;
  EXPIRE_DATE: string;
}

/**
 * Organization types in Thailand local government
 */
type OrgType = "อบต" | "เทศบาลตำบล" | "เทศบาลเมือง" | "เทศบาลนคร" | "อบจ" | "unknown";

/**
 * Extract organization type from name
 */
const getOrgType = (name: string): OrgType => {
  const normalized = name.replace(/\s+/g, "").replace(/\./g, "");
  if (normalized.includes("อบต")) return "อบต";
  if (normalized.includes("เทศบาลตำบล")) return "เทศบาลตำบล";
  if (normalized.includes("เทศบาลเมือง")) return "เทศบาลเมือง";
  if (normalized.includes("เทศบาลนคร")) return "เทศบาลนคร";
  if (normalized.includes("อบจ")) return "อบจ";
  return "unknown";
};

/**
 * Extract the base name (tambon/location name) without organization type prefix
 */
const extractBaseName = (name: string): string => {
  return name
    .replace(/\s+/g, "")
    .replace(/อบต\./g, "")
    .replace(/อบต/g, "")
    .replace(/เทศบาลตำบล/g, "")
    .replace(/เทศบาลเมือง/g, "")
    .replace(/เทศบาลนคร/g, "")
    .replace(/อบจ\./g, "")
    .replace(/อบจ/g, "")
    .trim()
    .toLowerCase();
};

/**
 * Normalize name for comparison by removing spaces and special characters
 * @deprecated Use getOrgType and extractBaseName for proper matching
 */
const normalizeName = (name: string): string => {
  return name
    .replace(/\s+/g, "")
    .replace(/อบต\./g, "อบต")
    .replace(/เทศบาลตำบล/g, "")
    .replace(/เทศบาลเมือง/g, "")
    .replace(/เทศบาลนคร/g, "")
    .trim()
    .toLowerCase();
};

/**
 * Fetch mayor information for a specific municipality
 * @param municipalityName - Name of the municipality from GeoJSON
 * @param provinceName - Province name (optional, helps narrow down search)
 */
export const fetchMayorInfo = async (
  municipalityName: string,
  provinceName?: string
): Promise<MayorInfo | null> => {
  const cacheKey = `${municipalityName}-${provinceName || ""}`;

  // Check cache first
  if (mayorCache.has(cacheKey)) {
    console.log(`Mayor info cache hit for: ${cacheKey}`);
    return mayorCache.get(cacheKey) || null;
  }

  console.log(
    `Fetching mayor info for: ${municipalityName}, ${provinceName || "N/A"}`
  );

  try {
    // Use cached all data if available
    let mayorData: MayorApiResponse[] = [];

    if (allMayorData) {
      mayorData = allMayorData;
      console.log(`Using cached mayor data: ${mayorData.length} records`);
    } else {
      // Fetch from local JSON file
      const response = await axios.get(MAYOR_DATA_FILE, {
        timeout: 10000,
        responseType: "json",
      });

      // The JSON file has structure: { status: 0, message: null, rows: [...] }
      if (response.data && Array.isArray(response.data.rows)) {
        mayorData = response.data.rows;
      } else if (Array.isArray(response.data)) {
        mayorData = response.data;
      } else {
        console.error("Invalid JSON file format");
        mayorCache.set(cacheKey, null);
        return null;
      }

      if (!mayorData || mayorData.length === 0) {
        console.error("Empty mayor data");
        mayorCache.set(cacheKey, null);
        return null;
      }

      // Cache all data for future requests
      allMayorData = mayorData;
      console.log(
        `Mayor data loaded from file: ${mayorData.length} records - cached for future use`
      );
    }

    // Extract organization type and base name from search term
    const searchOrgType = getOrgType(municipalityName);
    const searchBaseName = extractBaseName(municipalityName);
    console.log(`Searching for: type="${searchOrgType}", baseName="${searchBaseName}"`);

    // Try to find matching mayor with strict organization type matching
    const mayor = mayorData.find((item: MayorApiResponse) => {
      const itemOrgType = getOrgType(item.ORG_NAME);
      const itemBaseName = extractBaseName(item.ORG_NAME);
      const normalizedProvinceName = provinceName
        ? extractBaseName(item.PROVINCE_NAME)
        : "";
      const normalizedSearchProvince = provinceName
        ? extractBaseName(provinceName)
        : "";

      // First check: organization type must match (อบต must match อบต, เทศบาลตำบล must match เทศบาลตำบล)
      // If search term has a known org type, it must match exactly
      const orgTypeMatch = searchOrgType === "unknown" || itemOrgType === "unknown" || searchOrgType === itemOrgType;
      
      if (!orgTypeMatch) {
        return false;
      }

      // Second check: base name (tambon/location name) must match
      const baseNameMatch =
        itemBaseName === searchBaseName ||
        itemBaseName.includes(searchBaseName) ||
        searchBaseName.includes(itemBaseName);

      // If province is provided, also match province
      if (provinceName) {
        return baseNameMatch && normalizedProvinceName === normalizedSearchProvince;
      }

      return baseNameMatch;
    });

    if (mayor) {
      console.log(`Mayor found: ${mayor.NAME} from ${mayor.ORG_NAME} (type: ${getOrgType(mayor.ORG_NAME)})`);
      const mayorInfo: MayorInfo = {
        provinceName: mayor.PROVINCE_NAME,
        amphurName: mayor.AMPHUR_NAME,
        orgName: mayor.ORG_NAME,
        termYear: mayor.TERM_YEAR,
        name: mayor.NAME,
        startDate: mayor.START_DATE,
        expireDate: mayor.EXPIRE_DATE,
      };

      mayorCache.set(cacheKey, mayorInfo);
      return mayorInfo;
    }

    console.log(`No mayor found for: ${municipalityName}`);
    mayorCache.set(cacheKey, null);
    return null;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        console.error(
          "Mayor data file error:",
          error.response.status,
          error.response.data
        );
      } else if (error.request) {
        console.error("Mayor data file not found or not accessible");
      } else {
        console.error("Mayor data request error:", error.message);
      }
    } else {
      console.error("Error loading mayor data:", error);
    }
    mayorCache.set(cacheKey, null);
    return null;
  }
};

/**
 * Format date from API format to Thai display format
 */
export const formatThaiDate = (dateString: string): string => {
  if (!dateString) return "-";

  try {
    const date = new Date(dateString);
    const day = date.getDate();
    const year = date.getFullYear() + 543; // Convert to Buddhist year

    const thaiMonths = [
      "ม.ค.",
      "ก.พ.",
      "มี.ค.",
      "เม.ย.",
      "พ.ค.",
      "มิ.ย.",
      "ก.ค.",
      "ส.ค.",
      "ก.ย.",
      "ต.ค.",
      "พ.ย.",
      "ธ.ค.",
    ];

    return `${day} ${thaiMonths[date.getMonth()]} ${year}`;
  } catch (error) {
    return dateString;
  }
};

/**
 * Clear the mayor cache
 */
export const clearMayorCache = (): void => {
  mayorCache.clear();
};

/**
 * Check if mayor's term expires on November 27, 2025 (election upcoming in January)
 */
export const isMayorTermExpiringNov27 = (expireDate: string): boolean => {
  if (!expireDate) return false;

  try {
    const expiryDate = new Date(expireDate);
    const targetDate = new Date("2025-11-27"); // November 27, 2025

    // Compare only the date parts (ignore time)
    return (
      expiryDate.getFullYear() === targetDate.getFullYear() &&
      expiryDate.getMonth() === targetDate.getMonth() &&
      expiryDate.getDate() === targetDate.getDate()
    );
  } catch (error) {
    return false;
  }
};

/**
 * Load all mayor data and return Set of municipality IDs with expiring terms
 * This is much more efficient than checking each municipality individually
 */
export const getExpiringMayorMunicipalities = async (): Promise<
  Set<string>
> => {
  const expiringSet = new Set<string>();

  try {
    console.log("Loading mayor data to check for expiring terms...");

    // Fetch from local JSON file
    const response = await axios.get(MAYOR_DATA_FILE, {
      timeout: 10000,
      responseType: "json",
    });

    let mayorData: MayorApiResponse[] = [];

    if (response.data && Array.isArray(response.data.rows)) {
      mayorData = response.data.rows;
    } else if (Array.isArray(response.data)) {
      mayorData = response.data;
    } else {
      console.error("Invalid JSON file format");
      return expiringSet;
    }

    // Filter mayors with expiring terms (Nov 27, 2025)
    const expiringMayors = mayorData.filter((mayor) =>
      isMayorTermExpiringNov27(mayor.EXPIRE_DATE)
    );

    console.log(
      `Found ${expiringMayors.length} mayors with expiring terms on 27 พ.ย. 2568`
    );

    // Add normalized organization names to set for quick lookup
    expiringMayors.forEach((mayor) => {
      const normalizedName = normalizeName(mayor.ORG_NAME);
      expiringSet.add(normalizedName);
      // Also cache the full mayor info
      const cacheKey = `${mayor.ORG_NAME}-${mayor.PROVINCE_NAME}`;
      if (!mayorCache.has(cacheKey)) {
        mayorCache.set(cacheKey, {
          provinceName: mayor.PROVINCE_NAME,
          amphurName: mayor.AMPHUR_NAME,
          orgName: mayor.ORG_NAME,
          termYear: mayor.TERM_YEAR,
          name: mayor.NAME,
          startDate: mayor.START_DATE,
          expireDate: mayor.EXPIRE_DATE,
        });
      }
    });

    // Cache all data for future requests
    if (!allMayorData) {
      allMayorData = mayorData;
    }

    return expiringSet;
  } catch (error) {
    console.error("Error loading mayor data for expiring terms:", error);
    return expiringSet;
  }
};

/**
 * Get mayor info by municipality name and province (for checking expiry)
 */
export const getMayorByNameAndProvince = async (
  municipalityName: string,
  provinceName?: string
): Promise<MayorInfo | null> => {
  return fetchMayorInfo(municipalityName, provinceName);
};
